import { useState } from "react";
import { Link } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { openExternalUrl } from "@/utils/external-url";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { KanbanCard, KanbanEtapa } from "@/types/kanban";
import { AlertCircle, RefreshCw, Calendar, Globe } from "lucide-react";
import { Linkedin } from "@/components/icons/linkedin";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExternalLinkButton } from "@/components/ui/external-link-button";

interface DropInPlaceListModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cards: KanbanCard[];
  etapas: KanbanEtapa[];
  onReactivate: (card: KanbanCard, observacoes?: string) => Promise<void>;
  isLoading?: boolean;
  vagaId: string;
}

// Função para gerar iniciais do nome
const getInitials = (name: string): string => {
  const words = name.trim().split(' ').filter(word => word.length > 0);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

export function DropInPlaceListModal({
  open,
  onOpenChange,
  cards,
  etapas,
  onReactivate,
  isLoading,
  vagaId,
}: DropInPlaceListModalProps) {
  const [selectedCard, setSelectedCard] = useState<KanbanCard | null>(null);
  const [observacoes, setObservacoes] = useState("");
  const [isReactivating, setIsReactivating] = useState(false);

  const handleReactivate = async () => {
    if (!selectedCard) return;
    
    setIsReactivating(true);
    try {
      await onReactivate(selectedCard, observacoes);
      setSelectedCard(null);
      setObservacoes("");
    } finally {
      setIsReactivating(false);
    }
  };

  const handleCancel = () => {
    setSelectedCard(null);
    setObservacoes("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-orange-500" />
            Candidatos Descartados ({cards.length})
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(85vh-120px)]">
          <div className="space-y-4 pr-4">
            {cards.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <AlertCircle className="h-12 w-12 mb-3 text-slate-300" />
                <p className="text-lg font-medium">Nenhum candidato descartado</p>
                <p className="text-sm mt-1">Todos os candidatos estão visíveis nas etapas</p>
              </div>
            ) : (
              cards.map((card) => {
                const isSelected = selectedCard?.id === card.id;
                
                return (
                  <div
                    key={card.id}
                    className={`
                      p-4 rounded-lg border transition-all
                      ${isSelected 
                        ? 'border-blue-300 bg-blue-50/50 shadow-md' 
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-xs'}
                    `}
                  >
                    <div className="flex gap-4">
                      {/* Avatar */}
                      <div className="shrink-0">
                        <Link
                          to={`/freelancers/${card.freelancer.id}?fromVaga=${vagaId}&vagaTab=freelancers`}
                          className="block"
                        >
                          <Avatar className="cursor-pointer w-14 h-14 shadow-md border-2 border-white">
                            <AvatarFallback className="bg-linear-to-r from-orange-100 to-red-100 text-orange-700 text-base font-bold">
                              {getInitials(card.freelancer.nome)}
                            </AvatarFallback>
                          </Avatar>
                        </Link>
                      </div>
                      
                      {/* Informações */}
                      <div className="flex-1 min-w-0">
                        {/* Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h3 className="font-bold text-slate-900 text-base leading-tight">
                              <Link
                                to={`/freelancers/${card.freelancer.id}?fromVaga=${vagaId}&vagaTab=freelancers`}
                                className="cursor-pointer hover:text-blue-600 transition-colors"
                              >
                                {card.freelancer.nome}
                              </Link>
                            </h3>
                            {card.freelancer.areas && (
                              <p className="text-xs text-slate-500 mt-1">{card.freelancer.areas}</p>
                            )}
                          </div>
                          
                          {/* Links */}
                          <div className="flex items-center gap-1">
                            <ExternalLinkButton
                              kind="portfolio"
                              url={card.freelancer.portfolio_url}
                              hideWhenEmpty={false}
                              iconClassName="h-3 w-3"
                            />
                            <ExternalLinkButton
                              kind="linkedin"
                              url={card.freelancer.linkedin_url}
                              hideWhenEmpty={false}
                              iconClassName="h-4 w-4"
                            />
                          </div>
                        </div>

                        {/* Tempo descartado */}
                        <div className="flex items-center gap-3 text-xs text-slate-600 mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Descartado {formatDistanceToNow(new Date(card.updated_at), {
                              addSuffix: true,
                              locale: ptBR
                            })}
                          </span>
                        </div>

                        {/* Formulário de reativação - só mostra se selecionado */}
                        {isSelected ? (
                          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md space-y-3">
                            <div className="space-y-2">
                              <Label>Etapa de retorno</Label>
                              <div className="flex items-center gap-2 p-3 bg-white border border-slate-200 rounded-md">
                                <div
                                  className="w-3 h-3 rounded-full shrink-0"
                                  style={{ backgroundColor: card.etapa.cor }}
                                />
                                <span className="text-sm font-medium text-slate-700">
                                  {card.etapa.nome}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500">
                                O candidato será reativado nesta etapa onde foi descartado.
                              </p>
                            </div>
                            
                            <div className="space-y-2">
                              <Label htmlFor="observacoes">Observações (opcional)</Label>
                              <Textarea
                                id="observacoes"
                                placeholder="Adicione observações sobre a reativação..."
                                value={observacoes}
                                onChange={(e) => setObservacoes(e.target.value)}
                                rows={2}
                                className="bg-white"
                              />
                            </div>

                            <div className="flex items-center gap-2 justify-end">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleCancel}
                                disabled={isReactivating}
                              >
                                Cancelar
                              </Button>
                              <Button
                                size="sm"
                                onClick={handleReactivate}
                                disabled={isReactivating}
                              >
                                {isReactivating && <RefreshCw className="h-3 w-3 mr-1 animate-spin" />}
                                Reativar Candidato
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedCard(card)}
                            className="w-full mt-2"
                          >
                            <RefreshCw className="h-3 w-3 mr-2" />
                            Reativar Candidato
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

